// ═══════════════════════════════════════════════════════════════════════════
import { sliceRecordAtClosure } from './cageClosure';

// Multi-Profile Comparison — Analysis utilities
// ───────────────────────────────────────────────────────────────────────────
// Shared helpers used by MultiProfileComparison page and its sub-components.
// Keeps the page file thin and lets us unit-test these independently.
// ═══════════════════════════════════════════════════════════════════════════

// Okabe-Ito colour-vision-deficiency-safe palette — distinguishable for
// protanopia / deuteranopia / tritanopia users. Ordered by luminosity for
// better overlap readability.
export const PROFILE_COLORS = [
  { stroke: '#0072B2', fill: '#339EDB', name: 'Blue' },         // Okabe-Ito blue
  { stroke: '#D55E00', fill: '#E58A50', name: 'Vermillion' },   // Okabe-Ito vermillion
  { stroke: '#009E73', fill: '#43C19F', name: 'Green' },        // Okabe-Ito bluish-green
  { stroke: '#CC79A7', fill: '#DBA1BC', name: 'Pink' },         // Okabe-Ito reddish-purple
  { stroke: '#F0B400', fill: '#F5CC4D', name: 'Yellow' },       // Okabe-Ito yellow (adjusted)
  { stroke: '#56B4E9', fill: '#87C9EF', name: 'Sky' },          // Okabe-Ito sky blue
  { stroke: '#525252', fill: '#8C8C8C', name: 'Slate' },        // Neutral
  { stroke: '#7F3F98', fill: '#A070B5', name: 'Purple' },       // Tol purple
  { stroke: '#117733', fill: '#4DA368', name: 'Forest' },       // Tol forest
  { stroke: '#B10000', fill: '#D44A4A', name: 'Scarlet' },      // Deep red
];

export const AXIS_MODES = Object.freeze({
  DAYS: 'days',             // days since input
  CYCLE_PCT: 'cycle_pct',   // % of cycle completed
  WEIGHT: 'weight',         // body weight as x-axis
});

// ═══════════════════════════════════════════════════════════════════════════
// Display-name disambiguation
// ───────────────────────────────────────────────────────────────────────────
// `buildGrowthChartData`, `buildFeedChartData`, `buildMortalityChartData` and
// `computeProfileSummaries` all key their per-profile output on `record.name`
// (Recharts uses the same string as the `dataKey`). When two records share a
// name (e.g. the curated "Seabass example" plus a user-saved record someone
// re-named "Seabass example"), the second iteration overwrites the first —
// only one line ends up on the chart even though both KPI cards render.
//
// `disambiguateProfileNames` returns a SHALLOW-copied list where any duplicate
// `name` is suffixed with a sequential ` (1)` / ` (2)` / … so every record
// has a unique name without losing the human-readable label. Records keep
// their `id`, so this only affects the display name surfaces.
// ═══════════════════════════════════════════════════════════════════════════
export function disambiguateProfileNames(records) {
  if (!Array.isArray(records) || records.length === 0) return records || [];
  // Group indices by raw name (use empty-string fallback for unnamed records
  // — they get a numeric suffix anyway).
  const groups = new Map();
  records.forEach((r, idx) => {
    const raw = (r?.name || '').trim();
    if (!groups.has(raw)) groups.set(raw, []);
    groups.get(raw).push(idx);
  });
  // Build the rewritten list. A name only gets a suffix when it appears
  // more than once in the selection — single occurrences stay unchanged
  // so the legend stays clean for the common case.
  const out = records.map(r => ({ ...r }));
  groups.forEach((indices, raw) => {
    if (indices.length < 2) return;
    indices.forEach((origIdx, n) => {
      const base = raw || `Profile ${origIdx + 1}`;
      out[origIdx].name = `${base} (${n + 1})`;
    });
  });
  return out;
}


// ═══════════════════════════════════════════════════════════════════════════
// Species / cross-comparison checks
// ═══════════════════════════════════════════════════════════════════════════

export function detectSpeciesMismatch(selectedRecords) {
  const species = new Set(selectedRecords.map(r => (r?.species || '').toLowerCase().trim()).filter(Boolean));
  return { mixed: species.size > 1, species: Array.from(species) };
}

export function detectInputWeightSpread(selectedRecords) {
  const ws = selectedRecords.map(r => Number(r?.input_average_weight) || 0).filter(w => w > 0);
  if (ws.length < 2) return { wideSpread: false, min: 0, max: 0, ratio: 1 };
  const min = Math.min(...ws);
  const max = Math.max(...ws);
  const ratio = max / Math.max(min, 1);
  return { wideSpread: ratio >= 1.3, min, max, ratio };
}

// ═══════════════════════════════════════════════════════════════════════════
// Summary stats (extracted from MultiProfileComparison.js)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Compute per-profile summary statistics including SSOT-ready fields.
 * @param {Array} selectedRecords
 * @returns {Array}
 */
// ═══════════════════════════════════════════════════════════════════════════
// Per-profile summary computation
// ───────────────────────────────────────────────────────────────────────────
// Two modes (PFD-BUG-011):
//   • 'recorded' (default) — all metrics use the reported `input_number`.
//     Byte-identical to the pre-mode behaviour.
//   • 'adjusted'           — population-dependent metrics (bFCR, closing
//     biomass, feed efficiency) use `adjustedN = inputN − saved_analysis
//     .missing_fish`. Mortality %, SGR, latest weight, days-in-water,
//     total feed and mortality counts stay AS-RECORDED (per user rule
//     "preserve recorded feed, mortality, harvests, sample weights, SGR").
// Every row returns an `mfState` so the UI + CSV can label the mode's
// confidence per-cage: 'ok' | 'none' | 'unavailable' | 'invalid'.
//   - 'ok'          → MF ∈ (0, inputN)        → adjusted values shown
//   - 'none'        → MF === 0                → adjusted == recorded
//   - 'unavailable' → MF absent / stale null  → adjusted cells em-dash
//   - 'invalid'     → MF ≥ inputN             → adjusted cells em-dash + chip
// ═══════════════════════════════════════════════════════════════════════════
export function computeProfileSummaries(selectedRecords, { mode = 'recorded' } = {}) {
  return selectedRecords.map((record, idx) => {
    const lastSample = record.sample_points?.[record.sample_points.length - 1];
    const totalFeed = record.total_feed_given || 0;
    const totalMortality = record.total_mortalities || 0;
    const mortalityPct = record.input_number > 0
      ? ((totalMortality / record.input_number) * 100).toFixed(2)
      : 0;
    const latestWeight = lastSample?.weight || lastSample?.actual_weight_g || record.input_average_weight;

    // ── Missing-fish resolution ───────────────────────────────────────────
    // Same cascade used for the Multi-Profile Missing Fish column (PFD-BUG-010).
    // The chosen value defines the mode's adjustedN and `mfState`.
    const inputN = record.input_number || 0;
    const inputW = record.input_average_weight || 0;
    const mfRaw = (typeof record.saved_analysis?.missing_fish === 'number')
      ? record.saved_analysis.missing_fish
      : (typeof record.latest_missing_fish_count === 'number')
        ? record.latest_missing_fish_count
        : (typeof record.missing_fish_count === 'number' ? record.missing_fish_count : null);

    let mfState;      // 'ok' | 'none' | 'unavailable' | 'invalid'
    let adjustedN;
    if (mfRaw == null) {
      mfState = 'unavailable';
      adjustedN = inputN;         // placeholder; adjusted cells em-dash anyway
    } else if (mfRaw === 0) {
      mfState = 'none';
      adjustedN = inputN;         // identity → adjusted == recorded
    } else if (mfRaw >= inputN) {
      mfState = 'invalid';
      adjustedN = inputN;         // placeholder; adjusted cells em-dash
    } else {
      mfState = 'ok';
      adjustedN = inputN - mfRaw; // the single adjustedN derivation
    }

    // Only swap when the mode is active AND the estimate is usable.
    const useAdjusted = mode === 'adjusted' && mfState === 'ok';
    const effectiveN = useAdjusted ? adjustedN : inputN;

    let accBFCR = null;
    let closingBiomassKg = null;
    const trajectory = record.actual_trajectory || [];
    const dataPoints = record.data_points || [];

    if (effectiveN > 0 && inputW > 0 && totalFeed > 0 && trajectory.length > 0) {
      let mortalityBiomassKg = 0;
      let harvestBiomassKg = 0;
      let totalMortCount = 0;
      let totalHarvCount = 0;
      for (let i = 0; i < dataPoints.length; i++) {
        const dp = dataPoints[i];
        const mort = dp.mortality || 0;
        const harvN = dp.harvest_number || 0;
        const harvW = dp.harvest_weight || 0;
        const trajW = (i < trajectory.length) ? trajectory[i].weight : inputW;
        if (mort > 0) { totalMortCount += mort; mortalityBiomassKg += (mort * trajW) / 1000; }
        if (harvN > 0) {
          totalHarvCount += harvN;
          const hWeight = harvW > 0 ? harvW : trajW;
          harvestBiomassKg += (harvN * hWeight) / 1000;
        }
      }
      const remaining = Math.max(0, effectiveN - totalMortCount - totalHarvCount);
      const lastTrajW = trajectory[trajectory.length - 1]?.weight || 0;
      const startBiomass = (effectiveN * inputW) / 1000;
      closingBiomassKg = (remaining * lastTrajW) / 1000;
      const biomassGain = closingBiomassKg + harvestBiomassKg + mortalityBiomassKg - startBiomass;
      if (biomassGain > 0) accBFCR = totalFeed / biomassGain;
    }

    // SGR (Specific Growth Rate, %/day) — recorded growth, mode-independent.
    const daysInWater = record.days_in_water || 0;
    const sgr = (inputW > 0 && latestWeight > 0 && daysInWater > 0)
      ? ((Math.log(latestWeight) - Math.log(inputW)) / daysInWater) * 100
      : null;

    // Feed efficiency proxy (kg gain per kg feed) — "per fish" uses `effectiveN`
    // in adjusted mode so a hidden-loss cage no longer dilutes feed-per-fish
    // across phantom stock.
    const feedPerFish = effectiveN > 0 ? totalFeed / effectiveN : 0;
    const gainPerFishKg = (latestWeight - inputW) / 1000;
    const feedEfficiency = feedPerFish > 0 ? gainPerFishKg / feedPerFish : null;

    // In adjusted mode but no usable estimate → surface em-dash on
    // population-dependent cells (recorded cells untouched).
    const adjustedUnavailable = mode === 'adjusted' && mfState !== 'ok' && mfState !== 'none';

    return {
      id: record.id,
      name: record.name || `Profile ${idx + 1}`,
      color: PROFILE_COLORS[idx % PROFILE_COLORS.length],
      species: record.species || null,
      inputWeight: record.input_average_weight,
      latestWeight,
      daysInWater,
      totalFeed,
      totalMortality,
      mortalityPct,
      inputNumber: record.input_number,
      // PFD-BUG-011 mode outputs
      mode,
      mfState,
      adjustedFishCount: useAdjusted ? adjustedN : null,
      closingBiomassKg: adjustedUnavailable ? null : closingBiomassKg,
      accBFCR: adjustedUnavailable ? null : accBFCR,
      sgr,
      feedEfficiency: adjustedUnavailable ? null : feedEfficiency,
      // SSOT fields (from last MFI run stored on record, optional)
      actionability: record.latest_actionability || record.actionability || null,
      detectionStrength: record.latest_detection_strength || null,
      // ★ Missing-fish snapshot — read first from the saved individual
      //   analysis (same source Live Cage KPIs reads from, written by
      //   PUT /api/spreadsheet/records/{id}/saved-analysis). Falls back to
      //   the legacy `latest_missing_fish_count` / `missing_fish_count`
      //   top-level keys for defensive back-compat. When all three are
      //   absent the Multi-Profile column renders an em-dash with a tooltip
      //   so users know to open the profile to populate it. PFD-BUG-010.
      missingFishCount: mfRaw,
      dataReliabilityScore: (typeof record.latest_reliability_score === 'number')
        ? record.latest_reliability_score
        : (typeof record.data_reliability_score === 'number' ? record.data_reliability_score : null),
      hasExtremeFCR: !!record.latest_has_extreme_fcr,
    };
  });
}

// ═══════════════════════════════════════════════════════════════════════════
// Leader computation
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Identify the best performer for each metric. Returns a map {metric: profileId}.
 * @param {Array} summaries
 * @returns {{bFCR?: string, mortality?: string, sgr?: string, feedEfficiency?: string}}
 */
export function computeLeaders(summaries) {
  const leaders = {};
  if (!summaries?.length) return leaders;
  const validBFCR = summaries.filter(s => s.accBFCR != null && s.accBFCR > 0);
  if (validBFCR.length) leaders.bFCR = validBFCR.reduce((a, b) => (a.accBFCR < b.accBFCR ? a : b)).id;
  const validMort = summaries.filter(s => s.mortalityPct != null);
  if (validMort.length) leaders.mortality = validMort.reduce((a, b) => (parseFloat(a.mortalityPct) < parseFloat(b.mortalityPct) ? a : b)).id;
  const validSGR = summaries.filter(s => s.sgr != null && s.sgr > 0);
  if (validSGR.length) leaders.sgr = validSGR.reduce((a, b) => (a.sgr > b.sgr ? a : b)).id;
  const validEff = summaries.filter(s => s.feedEfficiency != null && s.feedEfficiency > 0);
  if (validEff.length) leaders.feedEfficiency = validEff.reduce((a, b) => (a.feedEfficiency > b.feedEfficiency ? a : b)).id;
  return leaders;
}

// ═══════════════════════════════════════════════════════════════════════════
// Chart data builders — axis-mode aware
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Build growth chart data. Supports 3 x-axis modes:
 *   - 'days'        → classic days-since-input
 *   - 'cycle_pct'   → 0..100% of cycle completed (per-profile normalization)
 *   - 'weight'      → x-axis is weight itself; each profile emits dots at its own sample points
 * Also supports y-axis normalization to input weight via `normalizeToInput`.
 */
export function buildGrowthChartData(selectedRecords, { axisMode = 'days', normalizeToInput = false } = {}) {
  // Apply the CLOSED-cage closing-endpoint rule uniformly before any
  // per-profile iteration. For CLOSED cages this truncates the trajectory
  // at the last registered harvest date; OPEN cages pass through unchanged.
  selectedRecords = (selectedRecords || []).map(sliceRecordAtClosure);
  if (!selectedRecords?.length) return [];

  if (axisMode === AXIS_MODES.WEIGHT) {
    // Weight-aligned: return one combined dataset where x=weight, y=days-to-reach.
    // Emit one row per sample across profiles to enable scatter/line overlap.
    const rows = [];
    selectedRecords.forEach((record, idx) => {
      const label = record.name || `Profile ${idx + 1}`;
      const traj = record.actual_trajectory || [];
      const step = Math.max(1, Math.floor(traj.length / 100));
      for (let d = 0; d < traj.length; d += step) {
        if (!traj[d]?.weight) continue;
        const w = normalizeToInput && record.input_average_weight
          ? traj[d].weight / record.input_average_weight
          : traj[d].weight;
        rows.push({ x: w, [`${label}`]: d });
      }
    });
    return rows.sort((a, b) => a.x - b.x);
  }

  // Find max "axis length"
  let maxLen = 0;
  selectedRecords.forEach(r => { if (r.actual_trajectory) maxLen = Math.max(maxLen, r.actual_trajectory.length); });
  if (maxLen === 0) return [];

  const interval = Math.max(1, Math.floor(maxLen / 150));
  const chartData = [];

  for (let day = 0; day <= maxLen; day += interval) {
    const point = { day };
    if (axisMode === AXIS_MODES.CYCLE_PCT) point.x = Math.round((day / Math.max(maxLen - 1, 1)) * 1000) / 10;
    else point.x = day;

    selectedRecords.forEach((record, idx) => {
      const label = record.name || `Profile ${idx + 1}`;
      const traj = record.actual_trajectory || [];
      let sourceDay = day;
      if (axisMode === AXIS_MODES.CYCLE_PCT) {
        // Use profile's own cycle length for cycle%
        sourceDay = Math.min(traj.length - 1, Math.round((day / Math.max(maxLen - 1, 1)) * Math.max(traj.length - 1, 0)));
      }
      if (traj[sourceDay]?.weight != null) {
        const w = normalizeToInput && record.input_average_weight
          ? traj[sourceDay].weight / record.input_average_weight
          : traj[sourceDay].weight;
        point[`${label}`] = Math.round(w * 100) / 100;
        if (traj[sourceDay].is_sample) point[`${label}_isSample`] = true;
      }
    });
    chartData.push(point);
  }
  return chartData;
}

export function buildFeedChartData(selectedRecords) {
  if (!selectedRecords?.length) return [];
  let maxDays = 0;
  selectedRecords.forEach(r => { if (r.data_points) maxDays = Math.max(maxDays, r.data_points.length); });
  if (maxDays === 0) return [];
  const interval = Math.max(1, Math.floor(maxDays / 100));
  const chartData = [];
  for (let day = 0; day <= maxDays; day += interval) {
    const point = { day };
    selectedRecords.forEach((record, idx) => {
      const label = record.name || `Profile ${idx + 1}`;
      let acc = 0;
      if (record.data_points) {
        for (let i = 0; i < Math.min(day, record.data_points.length); i++) {
          acc += record.data_points[i].feed || record.data_points[i].feed_given_kg || 0;
        }
      }
      point[`${label}`] = Math.round(acc);
    });
    chartData.push(point);
  }
  return chartData;
}

// ═══════════════════════════════════════════════════════════════════════════
// MONOTONIC-CUBIC (PCHIP) GROWTH SMOOTHING — SMOOTH CURVE TOGGLE
// ───────────────────────────────────────────────────────────────────────────
// The Multi-Profile "Growth Comparison" chart otherwise reads directly
// from each record's `actual_trajectory[]` — a backend-populated day-by-
// day series that reconstructs weights from feed/biomass between samples
// and *snaps* to sample weights on sample days, producing visible step
// artefacts. `buildSmoothGrowthChartData` re-anchors on the actual
// sample weights only and fills between them with a Fritsch-Carlson
// monotonic cubic Hermite interpolant (PCHIP), guaranteeing:
//   • passes EXACTLY through every sample-weight anchor
//   • no overshoot between anchors
//   • no artificial peaks (extrema only at anchors)
//   • no artificial dips (monotone stretches stay monotone)
//   • data-driven — NOT a chart-library cosmetic spline
// VISUAL / INTERPRETIVE ONLY: never mutates sample_points,
// actual_trajectory, saved_analysis, or any uploaded/imported field.
// ═══════════════════════════════════════════════════════════════════════════

function _pchipEvaluator(anchors) {
  const n = anchors.length;
  if (n === 0) return () => null;
  if (n === 1) return () => anchors[0].weight;
  const xs = anchors.map(a => a.day);
  const ys = anchors.map(a => a.weight);
  const dx = new Array(n - 1);
  const m  = new Array(n - 1);
  for (let i = 0; i < n - 1; i++) {
    dx[i] = xs[i + 1] - xs[i];
    if (dx[i] <= 0) dx[i] = 1;                       // defensive — sorted+deduped upstream
    m[i]  = (ys[i + 1] - ys[i]) / dx[i];
  }
  const t = new Array(n);
  t[0]     = m[0];
  t[n - 1] = m[n - 2];
  for (let i = 1; i < n - 1; i++) {
    if (m[i - 1] * m[i] <= 0) {
      t[i] = 0;                                       // sign change / flat → preserve monotonicity
    } else {
      const w1 = 2 * dx[i] + dx[i - 1];
      const w2 = dx[i] + 2 * dx[i - 1];
      t[i] = (w1 + w2) / (w1 / m[i - 1] + w2 / m[i]); // Fritsch-Carlson weighted harmonic
    }
  }
  for (let i = 0; i < n - 1; i++) {
    if (m[i] === 0) { t[i] = 0; t[i + 1] = 0; continue; }
    const a = t[i]     / m[i];
    const b = t[i + 1] / m[i];
    const h = a * a + b * b;
    if (h > 9) {
      const s = 3 / Math.sqrt(h);
      t[i]     = s * a * m[i];
      t[i + 1] = s * b * m[i];
    }
  }
  return (x) => {
    if (x <= xs[0])         return ys[0];
    if (x >= xs[n - 1])     return ys[n - 1];
    let i = 0;
    while (i < n - 1 && x > xs[i + 1]) i++;
    const h = xs[i + 1] - xs[i];
    const s = (x - xs[i]) / h;
    const h00 = (1 + 2 * s) * (1 - s) * (1 - s);
    const h10 = s * (1 - s) * (1 - s);
    const h01 = s * s * (3 - 2 * s);
    const h11 = s * s * (s - 1);
    return h00 * ys[i] + h10 * h * t[i] + h01 * ys[i + 1] + h11 * h * t[i + 1];
  };
}

function _extractGrowthAnchors(record) {
  // Anchors are (day-since-input, actual sample weight). `sample_points`
  // is the authoritative ground truth; `actual_trajectory` daily values
  // are NEVER used as anchors.
  const startIso = record?.start_date
    || record?.actual_trajectory?.[0]?.date
    || record?.sample_points?.[0]?.date;
  if (!startIso) return [];
  const startMs = new Date(startIso).getTime();
  if (!isFinite(startMs)) return [];
  const anchors = [];
  const inputW = Number(record?.input_average_weight);
  if (isFinite(inputW) && inputW > 0) anchors.push({ day: 0, weight: inputW });
  (record?.sample_points || []).forEach(sp => {
    const day = Math.round((new Date(sp?.date).getTime() - startMs) / 86400000);
    const w = Number(sp?.weight);
    if (isFinite(day) && day >= 0 && isFinite(w) && w > 0) {
      anchors.push({ day, weight: w });
    }
  });
  anchors.sort((a, b) => a.day - b.day);
  const dedup = [];
  for (const a of anchors) {
    if (dedup.length && dedup[dedup.length - 1].day === a.day) dedup[dedup.length - 1] = a;
    else dedup.push(a);
  }
  return dedup;
}

/**
 * buildSmoothGrowthChartData(selectedRecords)
 *   Same shape as `buildGrowthChartData(..., { axisMode: 'days' })`.
 *   Preserves the "Days Since Input" X-axis and series-per-profile
 *   labelling so profile colour + legend + selection continue to work.
 *   Guarantees: passes through every sample anchor exactly, no
 *   overshoot, no artificial peaks/dips.
 */
export function buildSmoothGrowthChartData(selectedRecords) {
  if (!selectedRecords?.length) return [];
  // Apply the shared CLOSED-cage closing-endpoint rule so smoothed
  // curves stop at the last registered harvest for closed cages.
  selectedRecords = selectedRecords.map(sliceRecordAtClosure);
  const perProfile = [];
  let maxDay = 0;
  selectedRecords.forEach((record, idx) => {
    const label = record?.name || `Profile ${idx + 1}`;
    const anchors = _extractGrowthAnchors(record);
    if (anchors.length === 0) return;
    const lastDay = anchors[anchors.length - 1].day;
    if (lastDay > maxDay) maxDay = lastDay;
    perProfile.push({ label, anchors, lastDay, evaluate: _pchipEvaluator(anchors) });
  });
  if (perProfile.length === 0 || maxDay === 0) return [];

  const interval = Math.max(1, Math.floor(maxDay / 200));
  const daySet = new Set();
  for (let d = 0; d <= maxDay; d += interval) daySet.add(d);
  daySet.add(maxDay);
  perProfile.forEach(p => p.anchors.forEach(a => daySet.add(a.day)));
  const days = Array.from(daySet).sort((a, b) => a - b);

  const rows = [];
  for (const day of days) {
    const point = { day };
    perProfile.forEach(p => {
      if (day > p.lastDay) return;                     // never extrapolate past last sample
      const w = p.evaluate(day);
      if (w != null && isFinite(w)) {
        point[p.label] = Math.round(w * 100) / 100;
        if (p.anchors.some(a => a.day === day)) point[`${p.label}_isSample`] = true;
      }
    });
    rows.push(point);
  }
  return rows;
}


export function buildMortalityChartData(selectedRecords) {
  if (!selectedRecords?.length) return [];
  let maxDays = 0;
  selectedRecords.forEach(r => { if (r.data_points) maxDays = Math.max(maxDays, r.data_points.length); });
  if (maxDays === 0) return [];
  const interval = Math.max(1, Math.floor(maxDays / 100));
  const chartData = [];
  for (let day = 0; day <= maxDays; day += interval) {
    const point = { day };
    selectedRecords.forEach((record, idx) => {
      const label = record.name || `Profile ${idx + 1}`;
      let acc = 0;
      if (record.data_points) {
        for (let i = 0; i < Math.min(day, record.data_points.length); i++) acc += record.data_points[i].mortality || 0;
      }
      point[`${label}`] = acc;
    });
    chartData.push(point);
  }
  return chartData;
}

// ═══════════════════════════════════════════════════════════════════════════
// Median / IQR ribbon for overlay charts
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Given chart rows of the form {day, ProfileA: 100, ProfileB: 120, ...}, compute
 * the per-x median, p25, p75. Returns rows augmented with _median, _p25, _p75.
 */
export function addDistributionRibbon(rows, seriesKeys) {
  if (!rows?.length || !seriesKeys?.length) return rows || [];
  return rows.map(r => {
    const vals = seriesKeys.map(k => r[k]).filter(v => typeof v === 'number' && isFinite(v)).sort((a, b) => a - b);
    if (vals.length < 2) return r;
    const pick = (p) => {
      const idx = Math.min(vals.length - 1, Math.max(0, Math.round(p * (vals.length - 1))));
      return vals[idx];
    };
    return { ...r, _p25: pick(0.25), _median: pick(0.5), _p75: pick(0.75) };
  });
}

// ═══════════════════════════════════════════════════════════════════════════
// Differential (A vs B) analysis
// ═══════════════════════════════════════════════════════════════════════════

export function computeDifferential(recordA, recordB) {
  if (!recordA || !recordB) return null;
  const trA = recordA.actual_trajectory || [];
  const trB = recordB.actual_trajectory || [];
  const maxLen = Math.min(trA.length, trB.length);
  const rows = [];
  const step = Math.max(1, Math.floor(maxLen / 60));
  for (let d = 0; d < maxLen; d += step) {
    const wA = trA[d]?.weight ?? null;
    const wB = trB[d]?.weight ?? null;
    if (wA == null || wB == null) continue;
    rows.push({
      day: d,
      weightA: Math.round(wA * 100) / 100,
      weightB: Math.round(wB * 100) / 100,
      deltaWeight: Math.round((wA - wB) * 100) / 100,
    });
  }
  // Overall deltas
  const feedA = recordA.total_feed_given || 0;
  const feedB = recordB.total_feed_given || 0;
  const mortA = recordA.total_mortalities || 0;
  const mortB = recordB.total_mortalities || 0;
  const mortPctA = recordA.input_number > 0 ? (mortA / recordA.input_number) * 100 : 0;
  const mortPctB = recordB.input_number > 0 ? (mortB / recordB.input_number) * 100 : 0;
  // Gap trend: sign of (latest delta - earliest delta)
  const firstDelta = rows[0]?.deltaWeight ?? 0;
  const lastDelta = rows[rows.length - 1]?.deltaWeight ?? 0;
  const gapDirection = Math.abs(lastDelta) > Math.abs(firstDelta) + 1 ? 'widening' : (Math.abs(lastDelta) < Math.abs(firstDelta) - 1 ? 'closing' : 'stable');
  return {
    rows,
    summary: {
      deltaFeedKg: feedA - feedB,
      deltaMortality: mortA - mortB,
      deltaMortalityPct: Math.round((mortPctA - mortPctB) * 100) / 100,
      firstDeltaWeight: firstDelta,
      lastDeltaWeight: lastDelta,
      gapDirection,
    },
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// PDF narrative summary
// ═══════════════════════════════════════════════════════════════════════════

export function buildPdfNarrative(summaries, leaders, { speciesMismatch, wideInputSpread }) {
  const lines = [];
  const byId = (id) => summaries.find(s => s.id === id);
  if (speciesMismatch) lines.push(`Note: profiles span multiple species — direct metric comparison may not be biologically equivalent.`);
  if (wideInputSpread) lines.push(`Note: input weights vary by ≥30%; use the "% cycle" or "weight-aligned" axis for fair growth comparison.`);
  if (leaders.bFCR) {
    const l = byId(leaders.bFCR);
    if (l?.accBFCR != null) lines.push(`${l.name} leads on bFCR (${l.accBFCR.toFixed(2)}).`);
  }
  if (leaders.mortality) {
    const l = byId(leaders.mortality);
    if (l) lines.push(`${l.name} has the lowest mortality (${l.mortalityPct}%).`);
  }
  if (leaders.sgr) {
    const l = byId(leaders.sgr);
    if (l?.sgr != null) lines.push(`${l.name} has the fastest SGR (${l.sgr.toFixed(2)} %/day).`);
  }
  return lines;
}

// ═══════════════════════════════════════════════════════════════════════════
// CSV export
// ═══════════════════════════════════════════════════════════════════════════

export function summariesToCsv(summaries, { mode = 'recorded' } = {}) {
  const header = [
    'Profile', 'Species', 'Input Fish',
    // PFD-BUG-011 — expose the mode and (when applicable) the adjusted
    // fish count directly in the CSV so the row is self-describing.
    'Mode', 'Adjusted Fish',
    'Input Weight (g)', 'Latest Weight (g)', 'Days', 'Total Feed (kg)',
    'Mortality', 'Mortality %', 'Acc. bFCR', 'SGR (%/day)',
    'Closing Biomass (kg)', 'Missing Fish Estimate', 'Decision Class',
  ];
  const modeLabel = mode === 'adjusted' ? 'PFD-Adjusted' : 'Recorded';
  const rows = summaries.map(s => [
    s.name,
    s.species || '',
    s.inputNumber || '',
    modeLabel,
    s.adjustedFishCount != null ? s.adjustedFishCount : '',
    s.inputWeight,
    s.latestWeight,
    s.daysInWater,
    s.totalFeed,
    s.totalMortality,
    s.mortalityPct,
    s.accBFCR != null ? s.accBFCR.toFixed(2) : '',
    s.sgr != null ? s.sgr.toFixed(2) : '',
    s.closingBiomassKg != null ? s.closingBiomassKg.toFixed(1) : '',
    s.missingFishCount != null ? s.missingFishCount : '',
    s.actionability || '',
  ]);
  const escape = (v) => {
    const str = String(v ?? '');
    return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
  };
  return [header, ...rows].map(row => row.map(escape).join(',')).join('\n');
}

// ═══════════════════════════════════════════════════════════════════════════
// Saved comparison views (localStorage-based; small, per-user)
// ═══════════════════════════════════════════════════════════════════════════

const SAVED_VIEWS_KEY = 'mpc_saved_views_v1';

export function loadSavedViews(userKey) {
  try {
    const raw = localStorage.getItem(`${SAVED_VIEWS_KEY}:${userKey || 'anon'}`);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

export function saveSavedView(userKey, { name, recordIds }) {
  const views = loadSavedViews(userKey);
  const id = `v_${Date.now()}`;
  const next = [{ id, name, recordIds, createdAt: new Date().toISOString() }, ...views].slice(0, 20);
  try { localStorage.setItem(`${SAVED_VIEWS_KEY}:${userKey || 'anon'}`, JSON.stringify(next)); } catch { /* ignore */ }
  return next;
}

export function deleteSavedView(userKey, viewId) {
  const views = loadSavedViews(userKey).filter(v => v.id !== viewId);
  try { localStorage.setItem(`${SAVED_VIEWS_KEY}:${userKey || 'anon'}`, JSON.stringify(views)); } catch { /* ignore */ }
  return views;
}
